import dayjs from "dayjs";

import { apiFormat, ChangeCell, shortMonthAndYear, type ColumnsType } from "@similarweb/pro-logic-components";

import { SearchKeywordCell, TrafficShareWithVisits } from "app/components/React/Table/cells";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { percentageSignOnlyOrZeroFilter, roundNumberFilter } from "app/filters/ngFilters";
import { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";
import { PeriodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { KeywordsBreakdownSortState, KeywordsBreakdownTableRow } from "./keywordsBreakdownTableDataSelector";

export type IFromTo = {
    from: string;
    to: string;
};

export type IFromToComparable = IFromTo & {
    compareFrom: string;
    compareTo: string;
};

type GetTableColumnsParams = {
    periodOverPeriodType: IPeriodOverPeriodCompareType | null;
    sortedColumn: KeywordsBreakdownSortState;
    duration: IFromTo | IFromToComparable;
    isWindow?: boolean;
    totalCount: number;
};

const formatDate = (date: string, format: string) => dayjs(date, apiFormat).format(format);

const buildSearchTermTitle = (totalCount: number) => {
    const label = translate("demand.analysis.search.trends.breakdown.search.term");
    if (totalCount <= 0) {
        return label;
    }
    return `${label} (${roundNumberFilter()(totalCount)})`;
};

const getSearchTermColumn = (totalCount: number) => ({
    dataIndex: "SearchTerm" as const,
    title: buildSearchTermTitle(totalCount),
    tooltipSubtitle: translate("demand.analysis.search.trends.breakdown.search.term.tooltip"),
    sorter: true,
    align: "left" as const,
    render: (_value: string, row: KeywordsBreakdownTableRow) => (
        <SearchKeywordCell
            value={row.SearchTerm}
            row={row}
            openInNewTab={false}
            destinationState={"organicsearch_keywordAnalysis_overview_2"}
            navigatorParams={{
                ...proNavigator.getParams(),
                tab: EKWGeneratorTableTabs.relatedKeywords,
            }}
        />
    ),
});

const getVolumeShareColumn = (isDaily: boolean) => ({
    dataIndex: "VolumeShare" as const,
    title: translate("demand.analysis.search.trends.breakdown.volume.share"),
    tooltipSubtitle: translate(
        isDaily
            ? "demand.analysis.search.trends.breakdown.volume.share.daily.tooltip"
            : "demand.analysis.search.trends.breakdown.volume.share.tooltip",
    ),
    sorter: true,
    align: "left" as const,
    render: (value: number, row: KeywordsBreakdownTableRow) => (
        <TrafficShareWithVisits
            value={value}
            row={row}
            totalVisitsRowProp="MonthlyVolume"
            visitMinThreshold={0}
        />
    ),
});

const getPrevVolumeShareColumn = () => ({
    dataIndex: "PrevVolumeShare" as const,
    title: translate("demand.analysis.search.trends.breakdown.volume.share.prev"),
    tooltipSubtitle: translate("demand.analysis.search.trends.breakdown.volume.share.tooltip"),
    sorter: true,
    align: "left" as const,
    render: (value: number | undefined, row: KeywordsBreakdownTableRow) => (
        <TrafficShareWithVisits
            value={value}
            row={row}
            totalVisitsRowProp="PrevMonthlyVolume"
            visitMinThreshold={0}
            innerColor={CHART_COLORS.periodOverPeriod.Total[1]}
        />
    ),
});

const getCurrentVolumeShareColumn = () => ({
    dataIndex: "VolumeShare" as const,
    title: translate("demand.analysis.search.trends.breakdown.volume.share.current"),
    tooltipSubtitle: translate("demand.analysis.search.trends.breakdown.volume.share.tooltip"),
    sorter: true,
    align: "left" as const,
    render: (value: number, row: KeywordsBreakdownTableRow) => (
        <TrafficShareWithVisits
            value={value}
            row={row}
            totalVisitsRowProp="MonthlyVolume"
            visitMinThreshold={0}
        />
    ),
});

const buildChangeTooltip = (
    isPeriodOverPeriod: boolean,
    from: string,
    to: string,
    compareFrom?: string,
    compareTo?: string,
) => {
    if (isPeriodOverPeriod && compareFrom && compareTo) {
        return translate("search.trends.breakdown.change.period.over.period.tooltip", {
            from: formatDate(from, shortMonthAndYear),
            to: formatDate(to, shortMonthAndYear),
            compareFrom: formatDate(compareFrom, shortMonthAndYear),
            compareTo: formatDate(compareTo, shortMonthAndYear),
        });
    }
    return translate("demand.analysis.search.trends.breakdown.total.volume.change.tooltip", {
        from: formatDate(from, shortMonthAndYear),
        to: formatDate(to, shortMonthAndYear),
    });
};

const CHANGE_DECIMAL_PLACES = 2;

const formatChangeAsPercentage = (value: number | undefined): string | undefined => {
    if (value === undefined || Number.isNaN(value)) {
        return undefined;
    }
    return percentageSignOnlyOrZeroFilter()(Math.abs(value * 100), CHANGE_DECIMAL_PLACES);
};

const getChangeColumnTitle = (periodOverPeriodType: IPeriodOverPeriodCompareType | null) =>
    periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.YEAR
        ? translate("search.trends.breakdown.change.yoy")
        : translate("search.trends.breakdown.change");

const CHANGE_COLUMN_WIDTH = 100;
const YOY_CHANGE_COLUMN_WIDTH = 130;

const getChangeColumn = (
    field: "TotalVolumeChange" | "CompareVolumeChange",
    tooltip: string,
    periodOverPeriodType: IPeriodOverPeriodCompareType | null,
) => ({
    dataIndex: field,
    title: getChangeColumnTitle(periodOverPeriodType),
    tooltipSubtitle: tooltip,
    sorter: true,
    width:
        periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.YEAR
            ? YOY_CHANGE_COLUMN_WIDTH
            : CHANGE_COLUMN_WIDTH,
    align: "right" as const,
    render: (value: number | undefined) => (
        <ChangeCell
            value={value}
            formattedValue={formatChangeAsPercentage(value)}
        />
    ),
});

export const getTableColumns = ({
    periodOverPeriodType,
    sortedColumn,
    duration,
    isWindow = false,
    totalCount,
}: GetTableColumnsParams): ColumnsType<KeywordsBreakdownTableRow> => {
    const isPeriodOverPeriod = periodOverPeriodType != null && "compareFrom" in duration && "compareTo" in duration;

    const columns: ColumnsType<KeywordsBreakdownTableRow> = isPeriodOverPeriod
        ? [
              getSearchTermColumn(totalCount),
              getPrevVolumeShareColumn(),
              getCurrentVolumeShareColumn(),
              getChangeColumn(
                  "CompareVolumeChange",
                  buildChangeTooltip(true, duration.from, duration.to, duration.compareFrom, duration.compareTo),
                  periodOverPeriodType,
              ),
          ]
        : [
              getSearchTermColumn(totalCount),
              getVolumeShareColumn(isWindow),
              getChangeColumn(
                  "TotalVolumeChange",
                  buildChangeTooltip(false, duration.from, duration.to),
                  periodOverPeriodType,
              ),
          ];

    return columns.map((column) => {
        const key = "dataIndex" in column ? column.dataIndex : undefined;
        const isSorted = key !== undefined && key === sortedColumn.columnKey;
        return {
            ...column,
            sortOrder: isSorted ? sortedColumn.order : null,
        };
    });
};
